/**
 * Toast Component (Enhanced v2.0)
 * Mobile-first notification system
 *
 * Features:
 * - Bottom-positioned on mobile with safe-area
 * - Swipe-to-dismiss gesture (100px threshold)
 * - X button with 44px touch target
 * - Group count display (e.g., "×3")
 * - Action buttons with 44px touch targets
 * - Auto-dismiss with configurable duration
 * - Dismissing animations (300ms fade-out)
 * - Stacks up to maxToasts (default 3)
 * - 4 variants: success/error/warning/info
 * - WCAG 2.1 AA compliant
 *
 * @version 2.0.0 - Enhanced for Notification UX Redesign
 */

import React, { useEffect, useState, useRef } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { useToastStore, Toast as ToastType } from '@shared/store/toastStore';
import { Portal } from './Portal';
import { hapticPresets } from '@shared/utils/haptics';
import { notificationMetrics } from '@shared/utils/notificationMetrics';

const variantStyles = {
  success: 'bg-green-900/85 backdrop-blur-lg border-green-600/50 text-green-100 shadow-lg shadow-green-900/20',
  error: 'bg-red-900/85 backdrop-blur-lg border-red-600/50 text-red-100 shadow-lg shadow-red-900/20',
  warning: 'bg-yellow-900/85 backdrop-blur-lg border-yellow-600/50 text-yellow-100 shadow-lg shadow-yellow-900/20',
  info: 'bg-blue-900/85 backdrop-blur-lg border-blue-600/50 text-blue-100 shadow-lg shadow-blue-900/20',
};

const variantIcons = {
  success: <CheckCircle className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  info: <Info className="w-5 h-5" />,
};

function ToastItem({ toast }: { toast: ToastType }) {
  const { removeToast } = useToastStore();
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);

  // Track view on mount
  useEffect(() => {
    notificationMetrics.trackToastView(toast.id, toast.variant);
  }, [toast.id, toast.variant]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    setOffsetX(diff);
  };

  const handleTouchEnd = () => {
    // Dismiss if swiped more than 100px
    if (Math.abs(offsetX) > 100) {
      hapticPresets.swipeEnd(); // Enhanced haptic feedback
      notificationMetrics.trackToastDismissManual(toast.id, toast.variant);
      removeToast(toast.id);
    }
    setOffsetX(0);
  };

  const handleManualDismiss = () => {
    notificationMetrics.trackToastDismissManual(toast.id, toast.variant);
    removeToast(toast.id);
  };

  const handleActionClick = () => {
    notificationMetrics.trackToastActionClick(toast.variant);
    toast.action!.onClick();
    removeToast(toast.id);
  };

  return (
    <div
      role="status"
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`
        ${variantStyles[toast.variant]}
        border rounded-xl shadow-lg
        p-4 mb-3 flex items-start gap-3
        backdrop-blur-sm
        ${toast.dismissing ? 'animate-slide-down opacity-0' : 'animate-slide-up'}
        transition-all duration-200
      `}
      style={{
        transform: `translateX(${offsetX}px)`,
        transition: offsetX === 0 ? 'transform 200ms ease-out, opacity 300ms ease-out' : 'none',
        opacity: toast.dismissing ? 0 : Math.max(0, 1 - Math.abs(offsetX) / 200),
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">
        {variantIcons[toast.variant]}
      </div>

      {/* Message */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          {toast.title && (
            <h4 className="text-sm font-semibold">{toast.title}</h4>
          )}
          {/* Group count badge */}
          {toast.count && toast.count > 1 && (
            <span className="text-xs opacity-70 font-medium">
              ×{toast.count}
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed opacity-90 mt-1">{toast.message}</p>

        {/* Action Button */}
        {toast.action && (
          <button
            onClick={handleActionClick}
            className="mt-2 text-sm font-medium underline min-h-[44px] inline-flex items-center hover:opacity-80 transition-opacity"
            aria-label={toast.action.label}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={handleManualDismiss}
        className="flex-shrink-0 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-black/20 rounded transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label="Dismiss notification"
        type="button"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts, clearAll } = useToastStore();

  // Keyboard support: Escape to dismiss all toasts
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && toasts.length > 0) {
        clearAll();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [toasts.length, clearAll]);

  if (toasts.length === 0) return null;

  return (
    <Portal>
      <div
        className="fixed bottom-0 left-0 right-0 z-toast p-4 pb-safe pointer-events-none"
        role="region"
        aria-label="Notifications"
        aria-live="polite"
      >
        <div className="max-w-md mx-auto pointer-events-auto">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} />
          ))}

          {/* Dismiss all hint (when 2+ toasts) */}
          {toasts.length >= 2 && (
            <div className="text-center mt-2">
              <button
                onClick={clearAll}
                className="text-xs text-dark-muted hover:text-white/70 transition-all duration-150 underline"
                aria-label="Dismiss all notifications"
              >
                Dismiss all ({toasts.length})
              </button>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
